import React, { useState, useMemo, useEffect } from 'react';

import { Segmented } from 'antd';

import { getMoment } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

import { useStatementContext, useStatementDispatch } from '../StatementContext';
import { StatementContainer } from '../StatementUI';
import { getFilteredStatementTotals } from '../StatementUtils';
import ReceivableBalances from './ReceivableBalances';
import ReceivableBody from './ReceivableBody';
import ReceivableFilters from './ReceivableFilters';
import { filterTransactions } from './ReceivableHelpers';
import ReceivableNavigation from './ReceivableNavigation';
import ReceivablePrintable from './ReceivablePrintable';
import ReceivableTools from './ReceivableTools';

/**
 * ReceivableWrapper
 *
 * Main wrapper component for receivable statements (client statements).
 * Handles filtering, balances display, and orchestrates sub-components.
 * Shows what clients owe to the agency.
 */
const ReceivableWrapper = ({ statement, isLoading }) => {
    const { contactID, contactType, loading, filters, alwaysEnabled, expand } = useStatementContext();

    const dispatch = useStatementDispatch();

    const onUpdateRange = (range) => {
        dispatch({ type: 'UpdateRange', value: range });
    };

    const onUpdatePeriod = (period) => {
        dispatch({ type: 'UpdateFilters', name: 'period', value: period });
    };

    const onUpdateExpand = (expand) => {
        dispatch({ type: 'UpdateExpand', value: expand });
    };

    const onUpdateClientStatus = (clientStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'clientStatus', value: clientStatus });
    };

    const onUpdatePaymentStatus = (paymentStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'paymentStatus', value: paymentStatus });
    };

    const onUpdateModelStatus = (modelStatus) => {
        dispatch({ type: 'UpdateFilters', name: 'modelStatus', value: modelStatus });
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [statementTypeFilter, setStatementTypeFilter] = useState('Real');
    const [previsionalStatusFilter, setPrevisionalStatusFilter] = useState('All');
    const [modelFilter, setModelFilter] = useState(null);
    const [modelList, setModelList] = useState([]);
    const [printDrawerVisible, setPrintDrawerVisible] = useState(false);

    const handleChangeSearchTerm = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearchTerm = () => {
        setSearchTerm('');
    };

    const configuration = useConfigurations();

    const handleOpenPrintDrawer = () => {
        setPrintDrawerVisible(true);
    };

    const handleClosePrintDrawer = () => {
        setPrintDrawerVisible(false);
    };

    const totalBalances = useMemo(() => {
        if (statement && statement.ClientBalances) {
            return statement.ClientBalances;
        }
        return {};
    }, [statement]);

    const availableBalances = useMemo(() => {
        if (statement && statement.AvailableClientBalances) {
            return statement.AvailableClientBalances;
        }
        return {};
    }, [statement]);

    // Generate model list from transactions
    useEffect(() => {
        if (statement && statement.Transactions && statement.Transactions.length > 0) {
            const modelListFromTransactions = Array.from(
                new Set(statement.Transactions.filter((t) => t.ModelID).map((t) => t.ModelID))
            ).map((id) => ({
                value: id,
                label: getContactName(id),
            }));
            setModelList(modelListFromTransactions);
        }
    }, [statement]);

    const activeTransactions = useMemo(() => {
        let filteredTransactions = [];
        if (statement && statement.Transactions && statement.Transactions.length > 0) {
            filteredTransactions = statement.Transactions;
            const ConfirmedStatuses = ['Billable', 'Locked', 'Invoiced'];
            const PrevisionalStatuses = ['Job', 'Option'];

            if (statementTypeFilter === 'Previsional') {
                switch (previsionalStatusFilter) {
                    case 'Option':
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => s.Status === 'Option')
                        );
                        break;
                    case 'Job':
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => s.Status === 'Job')
                        );
                        break;
                    default:
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => PrevisionalStatuses.includes(s.Status))
                        );
                        break;
                }
            } else {
                switch (filters.clientStatus) {
                    case 'All':
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => ConfirmedStatuses.includes(s.Status))
                        );
                        break;
                    default:
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => s.Status === filters.clientStatus)
                        );
                        break;
                }
                switch (filters.paymentStatus) {
                    case 'Paid':
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => s.PaidStatus === 'Paid')
                        );
                        break;
                    case 'NotPaid':
                        filteredTransactions = filteredTransactions.filter((tr) =>
                            tr.Statements.some((s) => s.PaidStatus !== 'Paid')
                        );
                        break;
                    default:
                        break;
                }
                switch (filters.modelStatus) {
                    case 'NotPaid':
                        filteredTransactions = filteredTransactions.filter((tr) => tr.ModelPaidStatus !== 'Paid');
                        break;
                    case 'Paid':
                        filteredTransactions = filteredTransactions.filter((tr) => tr.ModelPaidStatus === 'Paid');
                        break;
                    default:
                        break;
                }
            }

            // Apply model filter
            if (modelFilter) {
                filteredTransactions = filteredTransactions.filter((t) => t.ModelID === modelFilter);
            }

            if (searchTerm) {
                filteredTransactions = filterTransactions(filteredTransactions, searchTerm);
            }
        }
        return filteredTransactions;
    }, [statement, searchTerm, filters, previsionalStatusFilter, statementTypeFilter, modelFilter]);

    const StatementTotals = useMemo(() => {
        return getFilteredStatementTotals(activeTransactions, configuration.defaultCurrency, 'Client');
    }, [activeTransactions, configuration.defaultCurrency]);

    if (isLoading && !statement) {
        return (
            <StatementContainer>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            </StatementContainer>
        );
    }

    return (
        <StatementContainer>
            {statement && (
                <div className="statement-total-box-wrapper">
                    <div className="statement-filter">
                        <small>statement type</small>
                        <Segmented
                            value={statementTypeFilter}
                            onChange={setStatementTypeFilter}
                            size="small"
                            options={[
                                { value: 'Real', label: 'Confirmed' },
                                { value: 'Previsional', label: 'Pending' },
                            ]}
                        />
                    </div>
                    {statementTypeFilter === 'Real' ? (
                        <ReceivableBalances totalBalances={totalBalances} availableBalances={availableBalances} />
                    ) : (
                        <div className="statement-filter">
                            <small>job status</small>
                            <Segmented
                                value={previsionalStatusFilter}
                                onChange={setPrevisionalStatusFilter}
                                size="small"
                                options={[
                                    { value: 'All', label: 'All (Options/Jobs)' },
                                    { value: 'Job', label: 'Confirmed Job' },
                                    { value: 'Option', label: 'Option' },
                                ]}
                            />
                        </div>
                    )}
                </div>
            )}
            <ReceivableNavigation
                onUpdateRange={onUpdateRange}
                onUpdatePeriod={onUpdatePeriod}
                modelList={modelList}
                modelFilter={modelFilter}
                setModelFilter={setModelFilter}
                searchTerm={searchTerm}
                handleChangeSearchTerm={handleChangeSearchTerm}
                handleClearSearchTerm={handleClearSearchTerm}
            />
            {statementTypeFilter === 'Real' && (
                <ReceivableFilters
                    onUpdateClientStatus={onUpdateClientStatus}
                    onUpdatePaymentStatus={onUpdatePaymentStatus}
                    onUpdateModelStatus={onUpdateModelStatus}
                />
            )}
            {statement && (
                <ReceivableTools
                    onUpdateExpand={onUpdateExpand}
                    statementTypeFilter={statementTypeFilter}
                    handleOpenPrintDrawer={handleOpenPrintDrawer}
                />
            )}
            {statement && loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                    <Loading text="" textBlack />
                </div>
            )}
            {statement && (
                <ReceivableBody
                    transactions={activeTransactions}
                    isPrevisional={statementTypeFilter === 'Previsional'}
                    statementTotals={StatementTotals}
                />
            )}
            {printDrawerVisible && (
                <NwDrawer title="Print Statement" onClose={handleClosePrintDrawer} width="60vw" whiteBg={false}>
                    <ReceivablePrintable
                        balances={{
                            totalBalances: totalBalances,
                            availableBalances: availableBalances,
                        }}
                        transactions={activeTransactions}
                        onClose={handleClosePrintDrawer}
                        statementTotals={StatementTotals}
                    />
                </NwDrawer>
            )}
        </StatementContainer>
    );
};

export default ReceivableWrapper;
